import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Drawer,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  ViewList as ChronologicalIcon,
  ViewModule as GenerationIcon,
  ShowChart as TimelinePlotIcon,
  FileDownload as ExportIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Description as GedcomIcon,
  Assessment as StatsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { TimelineAggregationService } from '../services/timelineAggregationService';
import { TimelineExportService } from '../services/timelineExportService';
import { SystemConfigService } from '../services/systemConfigService';
import {
  TimelineEvent,
  TimelineFilter,
  TimelineViewMode,
  TimelineStatistics,
  TimelineExportFormat,
  TimelineExportOptions,
} from '../types/timeline';
import { LifeEventType, PersonSummary } from '../types/person';
import { PersonFilter } from '../components/timeline/PersonFilter';
import { EventTypeFilter } from '../components/timeline/EventTypeFilter';
import { DateRangeFilter } from '../components/timeline/DateRangeFilter';
import { GenerationFilter } from '../components/timeline/GenerationFilter';
import { ChronologicalView } from '../components/timeline/ChronologicalView';
import { GenerationGroupedView } from '../components/timeline/GenerationGroupedView';
import { TimelinePlotView } from '../components/timeline/TimelinePlotView';

const ALL_EVENT_TYPES: LifeEventType[] = [
  'birth',
  'death',
  'marriage',
  'divorce',
  'education',
  'employment',
  'military',
  'immigration',
  'residence',
  'achievement',
  'other',
];

/**
 * Timeline Page - Master Timeline showing all family events
 * Phase 3.3: Interactive Timeline Page
 */
const Timeline: React.FC = () => {
  const { authState } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureEnabled, setFeatureEnabled] = useState(false);

  // Data state
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [allEvents, setAllEvents] = useState<TimelineEvent[]>([]);
  const [statistics, setStatistics] = useState<TimelineStatistics | null>(null);

  // Filter state
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<LifeEventType[]>(ALL_EVENT_TYPES);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [selectedGenerations, setSelectedGenerations] = useState<number[]>([]);

  // View state
  const [viewMode, setViewMode] = useState<TimelineViewMode>('chronological');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(!isMobile);

  // Track if we're restoring from a share link to prevent auto-initialization
  const [isRestoringFromShare, setIsRestoringFromShare] = useState(false);

  // Export menu
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  // Check if feature is enabled
  useEffect(() => {
    const checkFeature = async () => {
      try {
        const config = await SystemConfigService.getConfig();
        setFeatureEnabled(config.features?.enableMasterTimeline ?? false);
      } catch (err) {
        console.error('Failed to check feature flag:', err);
        setFeatureEnabled(false);
      }
    };

    checkFeature();
  }, []);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!authState.user || !featureEnabled) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all events
        const events = await TimelineAggregationService.aggregateAllEvents(authState.user.role);
        console.log('📊 Timeline Debug: Loaded events:', events.length);
        console.log('📊 Event types breakdown:', events.reduce((acc, e) => {
          acc[e.type] = (acc[e.type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>));
        console.log('📊 Events WITH generation:', events.filter(e => e.generation !== undefined).length);
        console.log('📊 Events WITHOUT generation:', events.filter(e => e.generation === undefined).length);
        console.log('📊 Sample military events:', events.filter(e => e.type === 'military').map(e => ({
          type: e.type,
          person: e.personName,
          personId: e.personId,
          generation: e.generation,
          date: e.date,
        })));
        console.log('📊 Sample education events:', events.filter(e => e.type === 'education').map(e => ({
          type: e.type,
          person: e.personName,
          personId: e.personId,
          generation: e.generation,
          date: e.date,
        })));
        setAllEvents(events);

        // Extract unique people from events for the filter
        const uniquePeople = new Map<string, PersonSummary>();
        events.forEach((event) => {
          if (!uniquePeople.has(event.personId)) {
            // Convert 'unknown' gender to 'other' for PersonSummary compatibility
            const gender = event.personGender === 'unknown' ? 'other' : event.personGender;

            uniquePeople.set(event.personId, {
              id: event.personId,
              firstName: event.personName.split(' ')[0] || '',
              lastName: event.personName.split(' ').slice(1).join(' ') || '',
              birthDate: event.type === 'birth' ? event.date : undefined,
              gender: gender,
              isDeceased: event.type === 'death',
            });
          }
        });
        setPeople(Array.from(uniquePeople.values()));

        // Calculate statistics
        const stats = TimelineAggregationService.getTimelineStatistics(events);
        setStatistics(stats);

        // Initialize generation filter with all generations (only if not restoring from share link)
        if (stats.eventsByGeneration && !isRestoringFromShare) {
          const generations = Object.keys(stats.eventsByGeneration).map((g) => parseInt(g));
          setSelectedGenerations(generations);
        }
      } catch (err) {
        console.error('Failed to load timeline data:', err);
        setError('Failed to load timeline data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [authState.user, featureEnabled, isRestoringFromShare]);

  // Apply filters to events
  const filteredEvents = useMemo(() => {
    const filter: TimelineFilter = {
      personIds: selectedPersonIds.length > 0 ? selectedPersonIds : undefined,
      eventTypes: selectedEventTypes.length > 0 ? selectedEventTypes : undefined,
      startDate,
      endDate,
      generations: selectedGenerations.length > 0 ? selectedGenerations : undefined,
    };

    console.log('🔍 Filtering with:', {
      totalEvents: allEvents.length,
      selectedEventTypes,
      selectedPersonIds,
      selectedGenerations,
    });

    const result = TimelineAggregationService.filterEvents(allEvents, filter);

    console.log('🔍 Filter result:', {
      filteredCount: result.length,
      sampleFiltered: result.slice(0, 3).map(e => ({ type: e.type, person: e.personName, date: e.date })),
    });

    return result;
  }, [allEvents, selectedPersonIds, selectedEventTypes, startDate, endDate, selectedGenerations]);

  // Handle date range change
  const handleDateRangeChange = (start?: string, end?: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Handle view mode change
  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: TimelineViewMode | null) => {
    if (newMode) {
      setViewMode(newMode);
    }
  };

  // Export handlers
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = async (format: TimelineExportFormat) => {
    handleExportClose();

    try {
      const options: TimelineExportOptions = {
        format,
        includeStatistics: true,
        fileName: `family-timeline-${Date.now()}`,
      };

      const blob = await TimelineExportService.exportTimeline(filteredEvents, statistics, options);

      // Download file
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${options.fileName}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setError(`Failed to export timeline as ${format.toUpperCase()}`);
    }
  };

  // Share link handler
  const handleShare = () => {
    const filterState = {
      personIds: selectedPersonIds,
      eventTypes: selectedEventTypes,
      startDate,
      endDate,
      generations: selectedGenerations,
      viewMode,
      sortOrder,
    };

    const shareUrl = TimelineExportService.generateShareableLink(
      filterState,
      viewMode,
      window.location.origin
    );

    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        alert('Share link copied to clipboard!');
      },
      (err) => {
        console.error('Failed to copy share link:', err);
        alert('Failed to copy share link');
      }
    );
  };

  // Restore from share link on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareParam = params.get('share');

    if (shareParam) {
      // Mark that we're restoring from a share link to prevent auto-initialization of filters
      setIsRestoringFromShare(true);

      try {
        const decoded = TimelineExportService.decodeShareableLink(shareParam);

        if (decoded && decoded.filter) {
          const filter = decoded.filter;
          // Use !== undefined to allow empty arrays and falsy values to be restored
          if (filter.personIds !== undefined) setSelectedPersonIds(filter.personIds);
          if (filter.eventTypes !== undefined) setSelectedEventTypes(filter.eventTypes);
          if (filter.startDate !== undefined) setStartDate(filter.startDate);
          if (filter.endDate !== undefined) setEndDate(filter.endDate);
          if (filter.generations !== undefined) setSelectedGenerations(filter.generations);
          if (filter.sortOrder !== undefined) setSortOrder(filter.sortOrder);
        }
        // Also check top-level viewMode (for backwards compatibility)
        if (decoded && decoded.viewMode !== undefined) setViewMode(decoded.viewMode);
      } catch (err) {
        console.error('Failed to restore from share link:', err);
        // If restoration fails, allow normal initialization
        setIsRestoringFromShare(false);
      }
    }
  }, []);

  // Feature not enabled
  if (!featureEnabled && !loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Timeline Feature Not Enabled
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The master timeline feature is currently disabled. Please contact your administrator.
          </Typography>
        </Box>
      </Container>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading Timeline...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  // Filter panel content
  const filterPanel = (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Filters
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Stack spacing={3} divider={<Divider />}>
        {/* Person Filter */}
        <PersonFilter
          people={people}
          selectedPersonIds={selectedPersonIds}
          onChange={setSelectedPersonIds}
        />

        {/* Event Type Filter */}
        <EventTypeFilter
          events={allEvents}
          selectedTypes={selectedEventTypes}
          onChange={setSelectedEventTypes}
        />

        {/* Date Range Filter */}
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
        />

        {/* Generation Filter */}
        {statistics && (
          <GenerationFilter
            statistics={statistics}
            selectedGenerations={selectedGenerations}
            onChange={setSelectedGenerations}
          />
        )}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      {/* Filter Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sx={{
          width: 320,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 320,
            boxSizing: 'border-box',
            top: isMobile ? 0 : 64,
            height: isMobile ? '100%' : 'calc(100% - 64px)',
          },
        }}
      >
        {filterPanel}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="xl">
          {/* Page Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isMobile && (
                  <IconButton onClick={() => setFilterDrawerOpen(true)}>
                    <FilterIcon />
                  </IconButton>
                )}
                <TimelineIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="h4" component="h1" fontWeight={600}>
                  Master Family Timeline
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                  size="small"
                >
                  Share
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={handleExportClick}
                  size="small"
                >
                  Export
                </Button>
              </Stack>
            </Box>

            {/* Statistics Bar */}
            {statistics && (
              <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Events
                    </Typography>
                    <Typography variant="h6">{filteredEvents.length}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date Range
                    </Typography>
                    <Typography variant="body2">
                      {statistics.dateRange.earliest
                        ? `${statistics.dateRange.earliest.substring(0, 4)} - ${
                            statistics.dateRange.latest?.substring(0, 4) || 'Present'
                          }`
                        : 'No dates'}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      People
                    </Typography>
                    <Typography variant="h6">{statistics.peopleIncluded}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Generations
                    </Typography>
                    <Typography variant="h6">
                      {Object.keys(statistics.eventsByGeneration).length}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>

          {/* View Mode Toggle */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="medium">
              <ToggleButton value="chronological">
                <ChronologicalIcon sx={{ mr: 1 }} />
                Chronological
              </ToggleButton>
              <ToggleButton value="generation-grouped">
                <GenerationIcon sx={{ mr: 1 }} />
                By Generation
              </ToggleButton>
              <ToggleButton value="timeline-plot">
                <TimelinePlotIcon sx={{ mr: 1 }} />
                Timeline Plot
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Timeline Views */}
          <Paper sx={{ p: 3, minHeight: 600 }}>
            {viewMode === 'chronological' ? (
              <ChronologicalView
                events={filteredEvents}
                sortOrder={sortOrder}
                onSortOrderChange={setSortOrder}
              />
            ) : viewMode === 'generation-grouped' ? (
              <GenerationGroupedView events={filteredEvents} />
            ) : (
              <TimelinePlotView
                events={filteredEvents}
                sortOrder={sortOrder}
                onToggleSortOrder={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              />
            )}
          </Paper>
        </Container>
      </Box>

      {/* Export Menu */}
      <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={handleExportClose}>
        <MenuItem onClick={() => handleExport('pdf')}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('gedcom')}>
          <ListItemIcon>
            <GedcomIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as GEDCOM</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Timeline;
